import classNames from 'clsx';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameHeader, CasinoGameIcon, CasinoGameList, useWidgetAnchor } from '@swf/casino';
import type { CasinoIcon } from '@swf/casino';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import type { ShowField } from '@swf/core/types/CasinoGameList';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, isVisibleByAuth } from '@swf/core/utils/casino';

import styles from './CasinoGameSliderWidget.scss';
import { sliderWidgetModel, useSliderWidgetModel } from './model';

const CasinoGameSliderWidget: WidgetFC = () => {
  const { widgetSelect, widgetConfig } = useSliderWidgetModel();

  const { games, loading } = useSelector(widgetSelect.get);
  const icon =
    useSelector((state: RootState) => widgetSelect.getIcon(state, widgetConfig)) ||
    ('icnSquare' as CasinoIcon);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const fsbLaunch = useSelector((state: RootState) => widgetSelect.isFSB(state, widgetConfig));
  const fsb = widgetConfig.game_service === 'casino.games.myfsb';
  const {
    layout,
    display_title: displayTitle,
    sub_title: subTitle,
    min_games: minGames,
    hide_on_login: hideOnLogin,
    logged_in_only: loggedInOnly,
    more_link: moreLink,
    render_info: renderInfo,
    overlay_hierarchy: overlayHierarchy,
    rows,
    params
  } = widgetConfig;
  const widgetTargetId = useWidgetAnchor();
  const sliderRef = useMemo(() => React.createRef<SliderHandles>(), []);
  const containerRef = React.createRef<HTMLDivElement>();
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });

  const casinoGameHeaderProps = {
    title: displayTitle,
    subTitle,
    moreLink,
    icon,
    sliderRef,
    sliderState
  };
  const casinoGameListProps = {
    params,
    games,
    icon,
    rows,
    renderInfo: renderInfo as ShowField[],
    loading,
    onScroll: setSliderState,
    placeholder: fsb ? <CasinoGameIcon FSBPlaceholder /> : undefined,
    sliderRef,
    fsbLaunch,
    overlayHierarchy
  };

  return games?.length >= minGames &&
    isVisibleByAuth(loggedInOnly, hideOnLogin, isAuthenticated) ? (
    <section
      className={classNames(styles.container, styles[`${icon}Container` as keyof typeof styles])}
      style={getStyle(layout)}
      ref={containerRef}
      id={widgetTargetId}
    >
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Country, Header.Currency]} />
      {<CasinoGameHeader {...casinoGameHeaderProps} />}
      <CasinoGameList {...casinoGameListProps} />
    </section>
  ) : null;
};

CasinoGameSliderWidget.model = sliderWidgetModel;

export default CasinoGameSliderWidget;
